import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { promptText } from '../../lib/prompt-dialog.js';
import { entityName } from '../../lib/scene-doc.js';
import { attachProcgenSync, useProcgen } from '../../state/procgen.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import { AssetField } from '../scene/fields/AssetField.js';
import { FieldEditor } from '../scene/Inspector.js';
import { Card, ColorField, IconButton, JsonField, NumberField, PropRow, SelectField, TextField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { DependencyView } from './DependencyView.js';
import { ExpressionField } from './ExpressionField.js';
import { ParamsForm } from './ParamsForm.js';
import { PreviewPane } from './PreviewCanvas.js';
import {
  categoryNames,
  computeDepGraph,
  diagnosticsAt,
  formatJsonPath,
  freshName,
  FIELD_TYPES,
  getAtPath,
  inputPath,
  instanceName,
  isValidName,
  localSetNames,
  normalizeProp,
  OUTPUT_KINDS,
  parseJsonPath,
  PROCGEN_COMPONENT,
  PROCGEN_EXT,
  PROP_TYPES,
  randomSalt,
  renameKey,
  type CategoryRow,
  type InputDecl,
  type InputObject,
  type OutputDecl,
  type PatchDecl,
  type PlaceDecl,
  type ProcgenDiagnostic,
  type ProcgenDoc,
  type PropInput,
  type SpawnDecl,
} from './procgen-doc.js';
import { TableGrid } from './TableGrid.js';
import './procgen.css';

/**
 * The Procedural dock tool panel (`procgen`, engine/docs/systems/generators.md
 * section 10.4): generator files and the selected entity's roots on the
 * left, an inspector of the open generator in the middle, the preview on the
 * right. Every edit goes through `useProcgen` (undo, autosave, live scene).
 */

export const PROCGEN_PANEL = 'procgen';

const INPUT_SUFFIXES = [PROCGEN_EXT, '.terrainstream.json', '.terrain.json', '.scene.json'] as const;

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */

/** All diagnostics the panel knows (check + preview), deduplicated. */
function useAllDiagnostics(): ProcgenDiagnostic[] {
  const check = useProcgen((s) => s.diagnostics);
  const fromPreview = useProcgen((s) => s.preview.diagnostics);
  return useMemo(() => {
    const seen = new Set<string>();
    return [...check, ...fromPreview].filter((d) => {
      const key = `${d.code}|${d.file}|${d.path}|${d.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [check, fromPreview]);
}

/** An expression slot bound to a JSON path of the open generator. */
function Expr({ path, value, outputName, placeholder }: { path: string; value: string; outputName?: string; placeholder?: string }) {
  const file = useProcgen((s) => s.path) ?? '';
  const doc = useProcgen((s) => s.doc);
  const setAt = useProcgen((s) => s.setAt);
  const showExpression = useProcgen((s) => s.showExpression);
  const diagnostics = useAllDiagnostics();
  return (
    <ExpressionField
      value={value}
      onChange={(t) => setAt(path, t)}
      path={path}
      file={file}
      doc={doc}
      diagnostics={diagnostics}
      outputName={outputName}
      onShow={showExpression}
      placeholder={placeholder}
    />
  );
}

/** A number, or an expression (string) — the many slots that accept both. */
function NumOrExpr({ path, value, outputName, placeholder }: { path: string; value: unknown; outputName?: string; placeholder?: string }) {
  const setAt = useProcgen((s) => s.setAt);
  if (typeof value === 'string') {
    return (
      <div className="pcg-numexpr">
        <Expr path={path} value={value} outputName={outputName} placeholder={placeholder} />
      </div>
    );
  }
  return (
    <div className="pcg-numexpr">
      <NumberField value={typeof value === 'number' ? value : undefined} onChange={(v) => setAt(path, v)} />
      <button type="button" className="pcg-btn is-quiet" title="Use an expression" onClick={() => setAt(path, typeof value === 'number' ? String(value) : '0')}>
        fx
      </button>
      {value !== undefined && <IconButton icon="x" title="Remove" size={11} onClick={() => setAt(path, undefined)} />}
    </div>
  );
}

function Row({ label, hint, children, path }: { label: string; hint?: string; children: ReactNode; path?: string }) {
  return (
    <div data-pcg-path={path}>
      <PropRow label={label} hint={hint}>
        {children}
      </PropRow>
    </div>
  );
}

/** A name that commits on blur when valid (renames keep the key position). */
function NameInput({ value, onRename, label = 'name' }: { value: string; onRename: (next: string) => void; label?: string }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const ok = isValidName(text);
  return (
    <input
      className={`textfield mono pcg-name${ok ? '' : ' is-invalid'}`}
      value={text}
      aria-label={label}
      title={ok ? undefined : 'Names match [A-Za-z_][A-Za-z0-9_]* and are not reserved words (PG005)'}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== value && ok) onRename(text);
        else setText(value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setText(value);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** name → expression map (generator, output and patch `let`). */
function LetsEditor({ path, lets, outputName }: { path: string; lets: Record<string, string>; outputName?: string }) {
  const setAt = useProcgen((s) => s.setAt);
  const doc = useProcgen((s) => s.doc);
  return (
    <div className="pcg-lets" data-pcg-path={path}>
      {Object.entries(lets).map(([name, expr]) => (
        <div key={name} className="pcg-let">
          <NameInput value={name} onRename={(next) => setAt(path, renameKey(lets, name, next))} />
          <span className="pcg-eq">=</span>
          <Expr path={formatJsonPath([...parseJsonPath(path), name])} value={String(expr)} outputName={outputName} />
          <IconButton
            icon="trash"
            title={`Remove ${name}`}
            size={11}
            onClick={() => {
              const next = { ...lets };
              delete next[name];
              setAt(path, Object.keys(next).length ? next : undefined);
            }}
          />
        </div>
      ))}
      <button
        type="button"
        className="pcg-btn"
        onClick={() => setAt(path, { ...lets, [doc ? freshName(doc, 'v') : 'v']: '0' })}
      >
        <Icon name="plus" size={11} /> Let
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Left column                                                         */

function FileColumn() {
  const entries = useProjectFiles((s) => s.entries);
  const open = useProcgen((s) => s.open);
  const current = useProcgen((s) => s.path);
  const createNew = useProcgen((s) => s.createNew);
  const runtime = useProcgen((s) => s.runtimeStatus);
  const sceneDoc = useScene((s) => s.doc);
  const selection = useScene((s) => s.selection);
  const files = entries.filter((e) => e.kind === 'file' && e.path.toLowerCase().endsWith(PROCGEN_EXT)).map((e) => e.path).sort((a, b) => a.localeCompare(b));
  const entity = sceneDoc && selection[0] ? sceneDoc.entities.find((e) => e.id === selection[0]) : undefined;
  const component = entity?.components?.[PROCGEN_COMPONENT];
  const roots =
    component && typeof component === 'object' && Array.isArray((component as { graph?: unknown }).graph)
      ? ((component as { graph: unknown[] }).graph.filter((g): g is string => typeof g === 'string'))
      : [];

  return (
    <div className="pcg-files">
      <div className="pcg-subhead">
        Generators
        <button
          type="button"
          className="pcg-btn"
          title="New procedural generator (gen/*.procgen.json)"
          onClick={() =>
            void (async () => {
              const name = await promptText('Name of the new generator', 'New Generator');
              if (name?.trim()) await createNew('gen', name.trim());
            })()
          }
        >
          <Icon name="plus" size={11} /> New
        </button>
      </div>
      {files.length === 0 && <div className="muted pcg-empty-note">No *.procgen.json files yet.</div>}
      {files.map((f) => (
        <button key={f} type="button" className={`pcg-file${f === current ? ' is-active' : ''}`} title={f} onClick={() => void open(f)}>
          <Icon name="layers" size={12} />
          <span>{f.replace(/^.*\//, '').replace(/\.procgen\.json$/i, '')}</span>
          <span className="pcg-file-dir">{f.includes('/') ? f.slice(0, f.lastIndexOf('/')) : ''}</span>
        </button>
      ))}
      <div className="pcg-subhead">Selected entity</div>
      {!entity && <div className="muted pcg-empty-note">Select an entity in the scene to see its procgen roots.</div>}
      {entity && (
        <div className="pcg-entity">
          <div className="pcg-entity-name">{entityName(entity)}</div>
          {roots.length === 0 && (
            <div className="muted pcg-empty-note">
              {component ? 'Its procgen component lists no generators.' : 'No Procedural generation component.'}
              {current && (
                <button
                  type="button"
                  className="pcg-btn"
                  onClick={() => {
                    const value = component && typeof component === 'object' ? { ...(component as Record<string, unknown>), graph: [current] } : { graph: [current], seed: 1 };
                    useScene.getState().apply({ cmd: 'setComponent', id: entity.id, key: PROCGEN_COMPONENT, value });
                  }}
                >
                  Use {current.replace(/^.*\//, '')}
                </button>
              )}
            </div>
          )}
          {roots.map((r) => (
            <button key={r} type="button" className={`pcg-file${r === current ? ' is-active' : ''}`} title={r} onClick={() => void open(r)}>
              <Icon name="target" size={12} />
              <span>{r.replace(/^.*\//, '')}</span>
            </button>
          ))}
        </div>
      )}
      {runtime && (
        <>
          <div className="pcg-subhead">Runtime</div>
          <pre className="pcg-runtime mono">{JSON.stringify(runtime, null, 1).slice(0, 1200)}</pre>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Middle column                                                       */

function GeneralCard({ doc }: { doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const outputs = Object.keys(doc.outputs);
  return (
    <Card title="Generator" icon="layers" subtitle={doc.id}>
      <Row label="Id" hint="Instance name; unique in a graph (PG107)" path="id">
        <TextField value={doc.id} mono onChange={(v) => setAt('id', v.trim() || undefined)} />
      </Row>
      <Row label="Name" path="name">
        <TextField value={doc.name} onChange={(v) => setAt('name', v || undefined)} />
      </Row>
      <Row label="Description" path="description">
        <TextField value={doc.description} onChange={(v) => setAt('description', v)} />
      </Row>
      <Row label="Salt" hint="Seed identity: renaming or moving the file never changes the world" path="salt">
        <TextField value={doc.salt} mono onChange={(v) => setAt('salt', v.trim() || undefined)} />
        <IconButton icon="refresh" title="New random salt (changes every result)" size={12} onClick={() => setAt('salt', randomSalt())} />
      </Row>
      <Row label="Fixed seed" hint="Ignores the world seed (legacy reproduction)" path="seed">
        <NumberField value={typeof doc.seed === 'number' ? doc.seed : undefined} integer min={0} step={1} onChange={(v) => setAt('seed', v)} />
        {typeof doc.seed === 'number' && <IconButton icon="x" title="Use the world seed" size={11} onClick={() => setAt('seed', undefined)} />}
      </Row>
      <Row label="Default output" hint="What alias.x falls back to (section 5.6)" path="default">
        <SelectField value={doc.default} options={outputs} placeholder="(first output)" onChange={(v) => setAt('default', v || undefined)} />
      </Row>
      <Row label="Extent" hint='{ "circle": [cx, cz, r] } | { "rect": [x0, z0, x1, z1] } | { "box": [...] } (empty = infinite)' path="extent">
        <JsonField value={doc.extent ?? undefined} rows={1} onChange={(v) => setAt('extent', v)} />
      </Row>
      <Row label="Assets" hint="Extra files to preload, watch and check" path="assets">
        <FieldEditor def={{ key: 'assets', label: 'Assets', type: { kind: 'tags' } }} value={doc.assets ?? []} onChange={(v) => setAt('assets', Array.isArray(v) && v.length ? v : undefined)} />
      </Row>
    </Card>
  );
}

function FunctionsEditor({ doc }: { doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const fns = doc.functions ?? {};
  return (
    <div className="pcg-lets" data-pcg-path="functions">
      {Object.entries(fns).map(([name, fn]) => (
        <div key={name} className="pcg-fn">
          <div className="pcg-let">
            <NameInput value={name} onRename={(next) => setAt('functions', renameKey(fns, name, next))} />
            <span className="pcg-eq">(</span>
            <input
              className="textfield mono pcg-args"
              defaultValue={(fn.args ?? []).join(', ')}
              key={(fn.args ?? []).join(',')}
              aria-label={`${name} arguments`}
              onBlur={(e) => setAt(formatJsonPath(['functions', name, 'args']), e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            />
            <span className="pcg-eq">)</span>
            <IconButton
              icon="trash"
              title={`Remove ${name}`}
              size={11}
              onClick={() => {
                const next = { ...fns };
                delete next[name];
                setAt('functions', Object.keys(next).length ? next : undefined);
              }}
            />
          </div>
          <Expr path={formatJsonPath(['functions', name, 'expr'])} value={String(fn.expr ?? '')} />
        </div>
      ))}
      <button type="button" className="pcg-btn" onClick={() => setAt('functions', { ...fns, [freshName(doc, 'f')]: { args: ['v'], expr: 'v' } })}>
        <Icon name="plus" size={11} /> Function
      </button>
    </div>
  );
}

function InputsCard({ doc }: { doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const inputs = doc.inputs ?? {};
  const set = (alias: string, value: InputDecl | undefined) => setAt(formatJsonPath(['inputs', alias]), value);
  return (
    <Card title="Inputs" icon="link" subtitle={String(Object.keys(inputs).length)}>
      {Object.keys(inputs).length === 0 && <div className="muted pcg-empty-note">Other generators (or terrain / scene adapters) this one reads as alias.output.</div>}
      {Object.entries(inputs).map(([alias, input]) => {
        const obj: InputObject | null = typeof input === 'string' ? null : input;
        const adapter = obj !== null && typeof obj.source === 'string';
        const path = inputPath(input);
        return (
          <div key={alias} className="pcg-input" data-pcg-path={formatJsonPath(['inputs', alias])}>
            <div className="pcg-let">
              <NameInput value={alias} label="alias" onRename={(next) => setAt('inputs', renameKey(inputs, alias, next))} />
              <div className="pcg-input-path">
                <AssetField
                  spec={{ suffix: INPUT_SUFFIXES, label: 'Generator or adapter' }}
                  value={path}
                  onChange={(v) => {
                    const p = v ?? '';
                    if (!obj) set(alias, p);
                    else set(alias, adapter ? { ...obj, file: p } : { ...obj, path: p });
                  }}
                />
              </div>
              <IconButton icon="trash" title={`Remove ${alias}`} size={11} onClick={() => set(alias, undefined)} />
            </div>
            {obj && !adapter && (
              <Row label="Variant as" hint="Instance name of a variant (required with params, PG107)">
                <TextField value={typeof obj.as === 'string' ? obj.as : ''} mono onChange={(v) => set(alias, { ...obj, as: v.trim() || undefined })} />
              </Row>
            )}
            {obj && (
              <Row label={adapter ? 'Adapter' : 'Params'} hint={adapter ? 'Adapter keys (section 7.15)' : 'Param overrides; creates a variant instance'}>
                <JsonField
                  value={adapter ? Object.fromEntries(Object.entries(obj).filter(([k]) => k !== 'file')) : (obj.params ?? undefined)}
                  rows={1}
                  onChange={(v) => {
                    if (adapter) set(alias, { ...(v && typeof v === 'object' ? (v as InputObject) : {}), file: path } as InputObject);
                    else set(alias, { ...obj, params: v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined });
                  }}
                />
              </Row>
            )}
            {!obj && (
              <button type="button" className="pcg-btn is-quiet" onClick={() => set(alias, { path, params: {}, as: `${alias}_variant` })}>
                Make variant
              </button>
            )}
          </div>
        );
      })}
      <button type="button" className="pcg-btn" onClick={() => set(freshName(doc, 'input'), '')}>
        <Icon name="plus" size={11} /> Input
      </button>
    </Card>
  );
}

/* Outputs ----------------------------------------------------------- */

type PlaceKeyKind = 'numexpr' | 'num' | 'expr' | 'json' | 'text' | 'shape';

const PLACE_KEYS: Record<string, [string, PlaceKeyKind, string?][]> = {
  scatter: [
    ['density', 'numexpr', 'points per 100 m² (an expression needs maxDensity)'],
    ['maxDensity', 'num', 'constant upper bound'],
    ['jitter', 'num', '0..1 (0.8)'],
    ['yRange', 'json', '3D: [min, max]'],
  ],
  grid: [
    ['cell', 'json', 'number or [cx, cz]'],
    ['jitter', 'num'],
    ['offset', 'json'],
    ['yRange', 'json'],
  ],
  cells: [
    ['cell', 'json', 'number or [cx, cz]'],
    ['count', 'numexpr', 'per cell, at the cell centre'],
    ['maxCount', 'num', 'constant bound'],
    ['yRange', 'json'],
  ],
  around: [
    ['source', 'text', 'point set ref'],
    ['filter', 'expr', 'over it = source point'],
    ['count', 'numexpr', 'parent = source point'],
    ['maxCount', 'num'],
    ['radius', 'json', '[min, max] or an expression'],
    ['vertical', 'json', '3D [min, max]'],
  ],
  count: [
    ['count', 'num', 'exact target count'],
    ['region', 'json', 'default: extent'],
    ['candidates', 'json', 'placement object'],
    ['candidateDensity', 'num'],
  ],
  list: [
    ['items', 'json', '[{ "id"?, "x", "z", ... }]'],
    ['from', 'text', 'positions / shapes / polylines param'],
  ],
  fromPoints: [
    ['source', 'text', 'point set ref'],
    ['filter', 'expr'],
    ['shape', 'shape'],
    ['radius', 'numexpr'],
    ['size', 'numexpr'],
    ['yaw', 'numexpr'],
  ],
  script: [],
};

const PLACE_TYPES: Record<string, string[]> = {
  points: ['scatter', 'grid', 'cells', 'around', 'count', 'list', 'script'],
  areas: ['list', 'fromPoints', 'script'],
  paths: ['list', 'script'],
};

function PlaceForm({ name, output }: { name: string; output: OutputDecl }) {
  const setAt = useProcgen((s) => s.setAt);
  const base = formatJsonPath(['outputs', name, 'place']);
  const place: PlaceDecl = output.place && typeof output.place === 'object' ? output.place : { type: '' };
  const types = PLACE_TYPES[String(output.kind)] ?? [];
  return (
    <div className="pcg-place" data-pcg-path={base}>
      <Row label="Placement">
        <SelectField value={place.type || undefined} options={types} onChange={(t) => setAt(base, { type: t })} />
      </Row>
      {(PLACE_KEYS[place.type] ?? []).map(([key, kind, hint]) => {
        const path = `${base}.${key}`;
        const value = place[key];
        return (
          <Row key={key} label={key} hint={hint} path={path}>
            {kind === 'numexpr' ? (
              <NumOrExpr path={path} value={value} outputName={name} />
            ) : kind === 'num' ? (
              <>
                <NumberField value={typeof value === 'number' ? value : undefined} onChange={(v) => setAt(path, v)} />
                {value !== undefined && <IconButton icon="x" title="Remove" size={11} onClick={() => setAt(path, undefined)} />}
              </>
            ) : kind === 'expr' ? (
              <Expr path={path} value={typeof value === 'string' ? value : ''} outputName={name} placeholder="true" />
            ) : kind === 'text' ? (
              <TextField value={typeof value === 'string' ? value : ''} mono onChange={(v) => setAt(path, v.trim() || undefined)} />
            ) : kind === 'shape' ? (
              <SelectField value={typeof value === 'string' ? value : undefined} options={['circle', 'rect']} onChange={(v) => setAt(path, v)} />
            ) : (
              <JsonField value={value} rows={1} onChange={(v) => setAt(path, v)} />
            )}
          </Row>
        );
      })}
    </div>
  );
}

function WhereList({ name, output }: { name: string; output: OutputDecl }) {
  const setAt = useProcgen((s) => s.setAt);
  const path = formatJsonPath(['outputs', name, 'where']);
  const list = typeof output.where === 'string' ? [output.where] : Array.isArray(output.where) ? output.where.map(String) : [];
  const write = (next: string[]) => setAt(path, next.length === 0 ? undefined : next.length === 1 ? next[0] : next);
  return (
    <div className="pcg-where" data-pcg-path={path}>
      {list.map((w, i) => (
        <div key={i} className="pcg-let">
          <Expr path={typeof output.where === 'string' ? path : `${path}[${i}]`} value={w} outputName={name} />
          <IconButton icon="trash" title="Remove" size={11} onClick={() => write(list.filter((_, j) => j !== i))} />
        </div>
      ))}
      <button type="button" className="pcg-btn" onClick={() => write([...list, 'true'])}>
        <Icon name="plus" size={11} /> Where
      </button>
    </div>
  );
}

function AttributesTable({ name, output }: { name: string; output: OutputDecl }) {
  const setAt = useProcgen((s) => s.setAt);
  const path = formatJsonPath(['outputs', name, 'attributes']);
  const attrs = output.attributes ?? {};
  return (
    <div className="pcg-attrs" data-pcg-path={path}>
      {Object.entries(attrs).map(([attr, raw]) => {
        const p = normalizeProp(raw);
        const apath = `${path}.${attr}`;
        return (
          <div key={attr} className="pcg-attr" data-pcg-path={apath}>
            <div className="pcg-let">
              <NameInput value={attr} onRename={(next) => setAt(path, renameKey(attrs, attr, next))} />
              <SelectField value={p.type} options={PROP_TYPES.filter((t) => !['table', 'noise', 'positions', 'shapes', 'polylines', 'curve'].includes(t))} onChange={(t) => setAt(`${apath}.type`, t)} />
              {p.range && <span className="muted mono">[{p.range[0]}, {p.range[1]}]</span>}
              <IconButton icon="trash" title={`Remove ${attr}`} size={11} onClick={() => setAt(apath, undefined)} />
            </div>
            {typeof p.value === 'string' || p.value === undefined ? (
              <Expr path={`${apath}.value`} value={typeof p.value === 'string' ? p.value : ''} outputName={name} placeholder="value expression" />
            ) : (
              <JsonField value={p.value} rows={1} onChange={(v) => setAt(`${apath}.value`, v)} />
            )}
            {p.type === 'number' && (
              <Row label="Range" hint="Declared domain: values outside are clamped (PG805) and bound queries (section 5.10)">
                <JsonField value={p.range} rows={1} onChange={(v) => setAt(`${apath}.range`, v)} />
              </Row>
            )}
          </div>
        );
      })}
      <button type="button" className="pcg-btn" onClick={() => setAt(`${path}.${freshAttr(attrs)}`, { type: 'number', value: 'rand()' })}>
        <Icon name="plus" size={11} /> Attribute
      </button>
    </div>
  );
}

function freshAttr(attrs: Record<string, PropInput>): string {
  for (let i = 1; ; i++) if (!(`attr${i}` in attrs)) return `attr${i}`;
}

const TRANSFORM_KEYS = ['y', 'yaw', 'pitch', 'roll', 'scale', 'up', 'align'] as const;

function TransformForm({ name, output }: { name: string; output: OutputDecl }) {
  const path = formatJsonPath(['outputs', name, 'transform']);
  const t = output.transform ?? {};
  return (
    <div data-pcg-path={path}>
      {TRANSFORM_KEYS.map((k) => (
        <Row key={k} label={k} hint={k === 'y' ? 'height of 2D points' : k === 'up' ? 'vec3 the up axis tilts toward by align' : 'degrees'}>
          <NumOrExpr path={`${path}.${k}`} value={t[k]} outputName={name} />
        </Row>
      ))}
    </div>
  );
}

function SpawnForm({ name, output }: { name: string; output: OutputDecl }) {
  const setAt = useProcgen((s) => s.setAt);
  const path = formatJsonPath(['outputs', name, 'spawn']);
  const spawn: SpawnDecl | undefined = output.spawn;
  if (!spawn) {
    return (
      <button type="button" className="pcg-btn" onClick={() => setAt(path, { prefab: '', tags: [], distance: 180 })}>
        <Icon name="plus" size={11} /> Spawn block
      </button>
    );
  }
  const prefabExpr = spawn.prefab && typeof spawn.prefab === 'object' ? spawn.prefab.expr : null;
  const data = spawn.data ?? {};
  return (
    <div className="pcg-spawn" data-pcg-path={path}>
      <Row label="Prefab" hint="A literal path, or an asset-typed expression">
        {prefabExpr !== null ? (
          <Expr path={`${path}.prefab.expr`} value={String(prefabExpr)} outputName={name} />
        ) : (
          <AssetField spec={{ suffix: '.prefab.json' }} value={typeof spawn.prefab === 'string' ? spawn.prefab : ''} onChange={(v) => setAt(`${path}.prefab`, v ?? '')} />
        )}
        <button type="button" className="pcg-btn is-quiet" title="Literal path or expression" onClick={() => setAt(`${path}.prefab`, prefabExpr !== null ? '' : { expr: '' })}>
          {prefabExpr !== null ? 'path' : 'fx'}
        </button>
      </Row>
      <Row label="Tags" hint="Unioned with the prefab root's tags">
        <FieldEditor def={{ key: 'tags', label: 'Tags', type: { kind: 'tags' } }} value={spawn.tags ?? []} onChange={(v) => setAt(`${path}.tags`, v)} />
      </Row>
      <Row label="Data" hint="Merged into the prefab root's data component">
        <div className="pcg-lets">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="pcg-let">
              <NameInput value={key} onRename={(next) => setAt(`${path}.data`, renameKey(data, key, next))} />
              <span className="pcg-eq">=</span>
              {typeof value === 'string' ? (
                <Expr path={formatJsonPath(['outputs', name, 'spawn', 'data', key])} value={value} outputName={name} />
              ) : (
                <JsonField value={value} rows={1} onChange={(v) => setAt(formatJsonPath(['outputs', name, 'spawn', 'data', key]), v)} />
              )}
              <IconButton icon="trash" title={`Remove ${key}`} size={11} onClick={() => setAt(formatJsonPath(['outputs', name, 'spawn', 'data', key]), undefined)} />
            </div>
          ))}
          <button type="button" className="pcg-btn" onClick={() => setAt(`${path}.data`, { ...data, [`key${Object.keys(data).length + 1}`]: 'id' })}>
            <Icon name="plus" size={11} /> Data
          </button>
        </div>
      </Row>
      <Row label="Parent" hint="Scene entity id to parent spawned entities under">
        <TextField value={spawn.parent} mono onChange={(v) => setAt(`${path}.parent`, v.trim() || undefined)} />
      </Row>
      <Row label="Snap">
        <SelectField value={spawn.snap ?? 'none'} options={['none', 'ground']} onChange={(v) => setAt(`${path}.snap`, v === 'none' ? undefined : v)} />
      </Row>
      <Row label="Distance" hint="Spawn within this flat distance of viewers (0 = whole bounded region)">
        <NumberField value={spawn.distance} min={0} step={10} onChange={(v) => setAt(`${path}.distance`, v)} />
      </Row>
      <button type="button" className="pcg-btn is-danger" onClick={() => setAt(path, undefined)}>
        <Icon name="trash" size={11} /> Remove spawn
      </button>
    </div>
  );
}

function CategoriesForm({ name, output, doc }: { name: string; output: OutputDecl; doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const path = formatJsonPath(['outputs', name, 'categories']);
  const reserved = output.type === 'weights' ? ['color', 'weight', 'score'] : ['color', 'when'];
  if (typeof output.categories === 'string') {
    return (
      <Row label="Categories" hint='Another set ("biomes.zone"), an own output or "auto" (adapters)' path={path}>
        <TextField value={output.categories} mono onChange={(v) => setAt(path, v.trim() || [])} />
        <button type="button" className="pcg-btn is-quiet" onClick={() => setAt(path, [])}>
          inline
        </button>
      </Row>
    );
  }
  const rows = Array.isArray(output.categories) ? (output.categories as unknown[]) : [];
  return (
    <div data-pcg-path={path}>
      <div className="pcg-subhead">
        Categories <span className="muted">{categoryNames(rows).length}</span>
        <button type="button" className="pcg-btn is-quiet" title="Share another output's set" onClick={() => setAt(path, '')}>
          set ref
        </button>
      </div>
      <TableGrid
        path={path}
        doc={doc}
        columns={output.columns ?? {}}
        rows={rows}
        reserved={reserved}
        onRows={(next: CategoryRow[]) => setAt(path, next)}
        onColumns={(columns) => setAt(formatJsonPath(['outputs', name, 'columns']), Object.keys(columns).length ? columns : undefined)}
        setNames={(ref) => localSetNames(doc, ref)}
      />
    </div>
  );
}

function FieldForm({ name, output, doc }: { name: string; output: OutputDecl; doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const base = formatJsonPath(['outputs', name]);
  const type = output.type ?? 'number';
  const scripted = output.value === 'script';
  return (
    <>
      <Row label="Type" path={`${base}.type`}>
        <SelectField value={type} options={FIELD_TYPES} onChange={(v) => setAt(`${base}.type`, v)} />
      </Row>
      {type !== 'weights' && (
        <Row label="Value" hint='Expression; "script" means the generator script fills it (fill(ctx))' path={`${base}.value`}>
          {scripted ? (
            <span className="muted">filled by the script</span>
          ) : typeof output.value === 'string' || output.value === undefined ? (
            <Expr path={`${base}.value`} value={typeof output.value === 'string' ? output.value : ''} outputName={name} placeholder="fbm(freq: 0.01)" />
          ) : (
            <JsonField value={output.value} rows={1} onChange={(v) => setAt(`${base}.value`, v)} />
          )}
        </Row>
      )}
      <Row label="Cell" hint="Lattice spacing in meters; 0 / empty = exact (never cached)" path={`${base}.cell`}>
        {Array.isArray(output.cell) ? (
          <JsonField value={output.cell} rows={1} onChange={(v) => setAt(`${base}.cell`, v)} />
        ) : (
          <NumberField value={typeof output.cell === 'number' ? output.cell : undefined} min={0} step={0.5} onChange={(v) => setAt(`${base}.cell`, v || undefined)} />
        )}
      </Row>
      {type === 'number' && (
        <>
          <Row label="Store" path={`${base}.store`}>
            <SelectField value={output.store ?? 'f64'} options={['f64', 'f32']} onChange={(v) => setAt(`${base}.store`, v === 'f64' ? undefined : v)} />
          </Row>
          <Row label="Range" hint="[lo, hi]: declared domain (preview normalisation)" path={`${base}.range`}>
            <JsonField value={output.range} rows={1} onChange={(v) => setAt(`${base}.range`, v)} />
          </Row>
          <Row label="Unit" path={`${base}.unit`}>
            <TextField value={output.unit} onChange={(v) => setAt(`${base}.unit`, v || undefined)} />
          </Row>
        </>
      )}
      {type === 'weights' && (
        <>
          <Row label="Combine" path={`${base}.combine`}>
            <SelectField value={output.combine ?? 'normalize'} options={['normalize', 'stack', 'onehot', 'raw']} onChange={(v) => setAt(`${base}.combine`, v === 'normalize' ? undefined : v)} />
          </Row>
          <Row label="Fallback" hint='"none", a category name or "best"' path={`${base}.fallback`}>
            <TextField value={output.fallback} mono onChange={(v) => setAt(`${base}.fallback`, v.trim() || undefined)} />
          </Row>
          {output.combine === 'stack' && (
            <Row label="Stack" hint="base, steps [{ category, weight, mode, strength }], remainder, normalize" path={`${base}.steps`}>
              <JsonField value={output.steps} rows={2} onChange={(v) => setAt(`${base}.steps`, v)} />
            </Row>
          )}
        </>
      )}
      <Row label="Default" hint="Value outside the extent and for none / NaN (PG804)" path={`${base}.default`}>
        <JsonField value={output.default} rows={1} onChange={(v) => setAt(`${base}.default`, v)} />
      </Row>
      {(type === 'label' || type === 'weights') && <CategoriesForm name={name} output={output} doc={doc} />}
    </>
  );
}

function OutputEditor({ name, output, doc, diagnostics }: { name: string; output: OutputDecl; doc: ProcgenDoc; diagnostics: readonly ProcgenDiagnostic[] }) {
  const setAt = useProcgen((s) => s.setAt);
  const edit = useProcgen((s) => s.edit);
  const focus = useProcgen((s) => s.focus);
  const file = useProcgen((s) => s.path) ?? '';
  const setFocus = useProcgen((s) => s.setFocus);
  const base = formatJsonPath(['outputs', name]);
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!open) setFocus(base);
    setOpen(!open);
  };
  useEffect(() => {
    if (focus && (focus === base || focus.startsWith(`${base}.`) || focus.startsWith(`${base}[`))) setOpen(true);
  }, [focus, base]);
  const errors = diagnosticsAt(diagnostics, file, base, false).filter((d) => d.severity === 'error').length;
  const kind = String(output.kind);
  const isSet = kind === 'points' || kind === 'areas' || kind === 'paths';

  return (
    <div className={`pcg-output${open ? ' is-open' : ''}`} data-pcg-path={base}>
      <div className="pcg-output-head" role="button" tabIndex={0} onClick={toggle} onKeyDown={(e) => e.key === 'Enter' && toggle()}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
        <span className={`pcg-kind kind-${kind}`}>{kind}</span>
        <NameInput
          value={name}
          onRename={(next) =>
            edit((d) => ({ ...d, outputs: renameKey(d.outputs, name, next), ...(d.default === name ? { default: next } : {}) }))
          }
        />
        {output.type && <span className="muted mono">{output.type}</span>}
        {output.dims === '3d' && <span className="pcg-kind">3d</span>}
        {errors > 0 && <span className="pcg-errcount">{errors}</span>}
        <span className="pcg-spacer" />
        <IconButton
          icon="trash"
          title={`Remove ${name}`}
          size={11}
          onClick={() => setAt(base, undefined)}
        />
      </div>
      {open && (
        <div className="pcg-output-body">
          <Row label="Kind" path={`${base}.kind`}>
            <SelectField
              value={kind}
              options={OUTPUT_KINDS}
              onChange={(k) => setAt(base, k === 'field' ? { kind: k, type: 'number', value: '0', description: output.description } : { kind: k, place: { type: k === 'points' ? 'scatter' : 'list' }, description: output.description })}
            />
            <SelectField value={output.dims ?? '2d'} options={['2d', '3d']} onChange={(v) => setAt(`${base}.dims`, v === '2d' ? undefined : v)} />
          </Row>
          <Row label="Description" path={`${base}.description`}>
            <TextField value={output.description} onChange={(v) => setAt(`${base}.description`, v || undefined)} />
          </Row>
          <Row label="Color" hint="Preview and marker colour" path={`${base}.color`}>
            <ColorField value={output.color} alpha={false} onChange={(v) => setAt(`${base}.color`, v)} />
            {output.color && <IconButton icon="x" title="Default colour" size={11} onClick={() => setAt(`${base}.color`, undefined)} />}
          </Row>
          <Row label="Salt" hint="Seed key (default: the output name) so renaming keeps the randomness" path={`${base}.salt`}>
            <TextField value={output.salt} mono placeholder={name} onChange={(v) => setAt(`${base}.salt`, v.trim() || undefined)} />
          </Row>
          {kind === 'field' && <FieldForm name={name} output={output} doc={doc} />}
          {isSet && (
            <>
              <div className="pcg-subhead">Placement</div>
              <PlaceForm name={name} output={output} />
              <div className="pcg-subhead">Where</div>
              <WhereList name={name} output={output} />
            </>
          )}
          {kind === 'points' && (
            <>
              <Row label="Chance" hint="0..1: survives if its keyed rand() is below it" path={`${base}.chance`}>
                <NumOrExpr path={`${base}.chance`} value={output.chance} outputName={name} />
              </Row>
              <Row label="Footprint" hint="Item radius (m); enables overlaps() and auto spacing; must be bounded" path={`${base}.footprint`}>
                <NumOrExpr path={`${base}.footprint`} value={output.footprint} outputName={name} />
              </Row>
              <Row label="Spacing" hint='"auto", "none", a number or an expression over it' path={`${base}.spacing`}>
                {typeof output.spacing === 'string' && !['auto', 'none'].includes(output.spacing) ? (
                  <Expr path={`${base}.spacing`} value={output.spacing} outputName={name} />
                ) : typeof output.spacing === 'number' ? (
                  <NumberField value={output.spacing} min={0} onChange={(v) => setAt(`${base}.spacing`, v)} />
                ) : (
                  <SelectField value={typeof output.spacing === 'string' ? output.spacing : undefined} options={['auto', 'none']} placeholder="auto" onChange={(v) => setAt(`${base}.spacing`, v)} />
                )}
                <button type="button" className="pcg-btn is-quiet" onClick={() => setAt(`${base}.spacing`, typeof output.spacing === 'number' ? 'it.footprint' : 4)}>
                  {typeof output.spacing === 'number' ? 'fx' : 'm'}
                </button>
              </Row>
              <Row label="Prefer" hint="Among conflicting candidates the higher value wins" path={`${base}.prefer`}>
                <Expr path={`${base}.prefer`} value={typeof output.prefer === 'string' ? output.prefer : ''} outputName={name} />
              </Row>
              <Row label="Tile" hint="8..4096 m (64 in 2D)" path={`${base}.tile`}>
                <NumberField value={typeof output.tile === 'number' ? output.tile : undefined} min={8} max={4096} step={8} onChange={(v) => setAt(`${base}.tile`, v)} />
              </Row>
              <Row label="Max per tile" path={`${base}.maxPerTile`}>
                <NumberField value={output.maxPerTile} min={1} max={65536} step={256} integer onChange={(v) => setAt(`${base}.maxPerTile`, v)} />
              </Row>
            </>
          )}
          {kind === 'paths' && (
            <Row label="Width" path={`${base}.width`}>
              <NumOrExpr path={`${base}.width`} value={output.width} outputName={name} />
            </Row>
          )}
          {isSet && (
            <>
              <div className="pcg-subhead">Attributes</div>
              <AttributesTable name={name} output={output} />
            </>
          )}
          {kind === 'points' && (
            <>
              <div className="pcg-subhead">Transform</div>
              <TransformForm name={name} output={output} />
              <div className="pcg-subhead">Spawn</div>
              <SpawnForm name={name} output={output} />
            </>
          )}
          <div className="pcg-subhead">Output lets</div>
          <LetsEditor path={`${base}.let`} lets={output.let ?? {}} outputName={name} />
        </div>
      )}
    </div>
  );
}

function OutputsCard({ doc, diagnostics }: { doc: ProcgenDoc; diagnostics: readonly ProcgenDiagnostic[] }) {
  const setAt = useProcgen((s) => s.setAt);
  const [kind, setKind] = useState<string>('field');
  return (
    <Card title="Outputs" icon="data" subtitle={String(Object.keys(doc.outputs).length)}>
      {Object.entries(doc.outputs).map(([name, output]) => (
        <OutputEditor key={name} name={name} output={output} doc={doc} diagnostics={diagnostics} />
      ))}
      <div className="pcg-add-row">
        <SelectField value={kind} options={OUTPUT_KINDS} onChange={setKind} />
        <button
          type="button"
          className="pcg-btn"
          onClick={() => {
            const name = freshName(doc, kind === 'field' ? 'field' : kind);
            const value: OutputDecl =
              kind === 'field'
                ? { kind: 'field', type: 'number', value: 'fbm(freq: 0.01)' }
                : kind === 'points'
                  ? { kind: 'points', place: { type: 'scatter', density: 1 } }
                  : { kind, place: { type: 'list', items: [] } };
            setAt(formatJsonPath(['outputs', name]), value);
          }}
        >
          <Icon name="plus" size={11} /> Output
        </button>
      </div>
    </Card>
  );
}

function PatchesCard({ doc }: { doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const patches = doc.patches ?? [];
  const targets = [
    ...Object.keys(doc.outputs),
    ...Object.keys(doc.inputs ?? {}).map((a) => `${a}.`),
  ];
  const set = (i: number, value: PatchDecl | undefined) => {
    if (value === undefined) {
      const next = patches.filter((_, j) => j !== i);
      setAt('patches', next.length ? next : undefined);
    } else setAt(`patches[${i}]`, value);
  };
  return (
    <Card title="Patches" icon="edit" subtitle={String(patches.length)} defaultOpen={patches.length > 0}>
      <datalist id="pcg-patch-targets">
        {targets.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      {patches.length === 0 && <div className="muted pcg-empty-note">Change another generator's output (alias.output) without editing it.</div>}
      {patches.map((p, i) => {
        const mode = typeof p.remove === 'string' ? 'remove' : 'value';
        const path = `patches[${i}]`;
        return (
          <div key={i} className="pcg-patch" data-pcg-path={path}>
            <Row label="Target" hint="alias.output (an input's output) or an own output">
              <TextField value={p.target} mono list="pcg-patch-targets" placeholder="terrain.height" onChange={(v) => set(i, { ...p, target: v.trim() })} />
              <IconButton icon="trash" title="Remove patch" size={11} onClick={() => set(i, undefined)} />
            </Row>
            <Row label="Order" hint="(order, instance, index) order on one target">
              <NumberField value={p.order ?? 0} integer step={10} onChange={(v) => set(i, { ...p, order: v })} />
              <SelectField
                value={mode}
                options={['value', 'remove']}
                onChange={(m) => {
                  const { value: _v, remove: _r, ...rest } = p;
                  set(i, (m === 'remove' ? { ...rest, remove: 'false' } : { ...rest, value: 'prev' }) as PatchDecl);
                }}
              />
            </Row>
            <Row label={mode === 'remove' ? 'Remove' : 'Value'} hint={mode === 'remove' ? 'bool over the target item (it, x, y, z)' : 'new value; prev is the value before this patch'}>
              <Expr path={`${path}.${mode}`} value={String((mode === 'remove' ? p.remove : p.value) ?? '')} />
            </Row>
            <Row label="Description">
              <TextField value={p.description} onChange={(v) => set(i, { ...p, description: v || undefined })} />
            </Row>
            <div className="pcg-subhead">Patch lets</div>
            <LetsEditor path={`${path}.let`} lets={p.let ?? {}} />
          </div>
        );
      })}
      <button type="button" className="pcg-btn" onClick={() => setAt('patches', [...patches, { target: '', order: 100, value: 'prev' }])}>
        <Icon name="plus" size={11} /> Patch
      </button>
    </Card>
  );
}

function ScriptCard({ doc }: { doc: ProcgenDoc }) {
  const setAt = useProcgen((s) => s.setAt);
  const script = doc.script;
  return (
    <Card title="Script" icon="script" subtitle={script?.path ?? 'none'} defaultOpen={Boolean(script)}>
      {!script ? (
        <button type="button" className="pcg-btn" onClick={() => setAt('script', { path: `gen/${doc.id ?? 'generator'}.rhai`, anchor: 256, reach: 0, reads: [] })}>
          <Icon name="plus" size={11} /> Rhai script
        </button>
      ) : (
        <div data-pcg-path="script">
          <Row label="Path" hint="fn generate(ctx) and / or fn fill(ctx)">
            <AssetField spec={{ suffix: '.rhai' }} value={script.path} onChange={(v) => setAt('script.path', v ?? '')} />
          </Row>
          <Row label="Anchor" hint="Anchor cell size (m); generate runs once per anchor cell">
            <JsonField value={script.anchor} rows={1} onChange={(v) => setAt('script.anchor', v)} />
          </Row>
          <Row label="Reach" hint="How far outside its anchor cell a run may emit items">
            <NumberField value={script.reach} min={0} onChange={(v) => setAt('script.reach', v)} />
          </Row>
          <Row label="Input reach" hint="How far outside the cell the script may read inputs">
            <NumberField value={script.inputReach} min={0} onChange={(v) => setAt('script.inputReach', v)} />
          </Row>
          <Row label="Reads" hint='Every input ref the script uses ("terrain.slope")'>
            <FieldEditor def={{ key: 'reads', label: 'Reads', type: { kind: 'tags' } }} value={script.reads ?? []} onChange={(v) => setAt('script.reads', v)} />
          </Row>
          <Row label="Bounds" hint="A region: one bounded run over it (global algorithms)">
            <JsonField value={script.bounds ?? undefined} rows={1} onChange={(v) => setAt('script.bounds', v)} />
          </Row>
          <Row label="Max operations">
            <NumberField value={script.maxOperations} min={1} step={100000} integer onChange={(v) => setAt('script.maxOperations', v)} />
          </Row>
          <button type="button" className="pcg-btn is-danger" onClick={() => setAt('script', undefined)}>
            <Icon name="trash" size={11} /> Remove script
          </button>
        </div>
      )}
    </Card>
  );
}

/** Scrolls the inspector to a JSON path (and opens its output). */
function reveal(path: string): void {
  useProcgen.getState().setFocus(path);
  if (typeof document === 'undefined') return;
  requestAnimationFrame(() => {
    let p = path;
    for (;;) {
      const el = document.querySelector(`[data-pcg-path="${p.replace(/"/g, '\\"')}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.add('pcg-flash');
        setTimeout(() => el.classList.remove('pcg-flash'), 1200);
        return;
      }
      const cut = Math.max(p.lastIndexOf('.'), p.lastIndexOf('['));
      if (cut <= 0) return;
      p = p.slice(0, cut);
    }
  });
}

function DiagnosticsCard({ diagnostics, file }: { diagnostics: readonly ProcgenDiagnostic[]; file: string }) {
  const checking = useProcgen((s) => s.checking);
  const source = useProcgen((s) => s.checkSource);
  const check = useProcgen((s) => s.check);
  const open = useProcgen((s) => s.open);
  const errors = diagnostics.filter((d) => d.severity === 'error').length;
  const warnings = diagnostics.filter((d) => d.severity === 'warning').length;
  return (
    <Card
      title="Diagnostics"
      icon={errors ? 'error' : warnings ? 'warning' : 'check'}
      subtitle={checking ? 'checking…' : `${errors} errors · ${warnings} warnings${source ? ` · ${source}` : ''}`}
      actions={<IconButton icon="refresh" title="Check again" size={12} onClick={() => void check()} />}
    >
      {diagnostics.length === 0 && <div className="muted pcg-empty-note">{checking ? 'Checking…' : 'No problems.'}</div>}
      {diagnostics.map((d, i) => {
        const other = d.file && d.file !== file;
        return (
          <div key={i} className={`pcg-diag is-${d.severity}`}>
            <Icon name={d.severity === 'error' ? 'error' : d.severity === 'warning' ? 'warning' : 'info'} size={12} />
            <div className="pcg-diag-body">
              <div>
                <span className="pcg-code">{d.code}</span>{' '}
                <button
                  type="button"
                  className="pcg-link mono"
                  title={other ? `Open ${d.file}` : 'Show in the inspector'}
                  onClick={() => (other ? void open(d.file).then(() => reveal(d.path)) : reveal(d.path))}
                >
                  {other ? `${d.file}: ` : ''}
                  {d.path || '(file)'}
                </button>
              </div>
              <div>{d.message}</div>
              {d.help && <div className="muted">help: {d.help}</div>}
              {(d.related ?? []).map((r, j) => (
                <button key={j} type="button" className="pcg-link muted" onClick={() => (r.file && r.file !== file ? void open(r.file).then(() => reveal(r.path)) : reveal(r.path))}>
                  note: {r.file}: {r.path} {r.message}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function RawJsonCard({ text, parseError }: { text: string; parseError: string | null }) {
  const setText = useProcgen((s) => s.setText);
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  return (
    <Card title="JSON" icon="code" subtitle={parseError ? 'syntax error' : 'source'} defaultOpen={Boolean(parseError)}>
      {parseError && <div className="pcg-error">PG001: {parseError}</div>}
      <textarea
        className="pcg-raw mono"
        spellCheck={false}
        value={draft}
        rows={Math.min(30, Math.max(8, draft.split('\n').length))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== text) setText(draft);
        }}
        aria-label="Generator JSON"
      />
    </Card>
  );
}

function Inspector() {
  const path = useProcgen((s) => s.path);
  const doc = useProcgen((s) => s.doc);
  const text = useProcgen((s) => s.text);
  const parseError = useProcgen((s) => s.parseError);
  const loading = useProcgen((s) => s.loading);
  const error = useProcgen((s) => s.error);
  const graphInfo = useProcgen((s) => s.graphInfo);
  const diagnostics = useAllDiagnostics();

  const deps = useMemo(() => (doc && path ? (graphInfo ?? computeDepGraph(doc, instanceName(doc, path), diagnostics)) : null), [doc, path, graphInfo, diagnostics]);

  if (!path) {
    return (
      <div className="pcg-inspector pcg-empty">
        <Icon name="layers" size={28} />
        <strong>No generator open</strong>
        <span className="muted">Pick a *.procgen.json on the left, or create one with New.</span>
      </div>
    );
  }
  if (loading) return <div className="pcg-inspector muted pcg-empty-note">Loading {path}…</div>;
  if (error) return <div className="pcg-inspector pcg-error">{error}</div>;
  const instance = doc ? instanceName(doc, path) : '';

  return (
    <div className="pcg-inspector">
      {parseError && <div className="pcg-error">The file is not valid JSON: {parseError}. Fix it in the JSON card below.</div>}
      {doc && (
        <>
          <GeneralCard doc={doc} />
          <Card title="Params" icon="settings" subtitle={String(Object.keys(doc.params ?? {}).length)}>
            <ParamsForm doc={doc} file={path} diagnostics={diagnostics} />
          </Card>
          <Card title="Lets & functions" icon="code" subtitle={String(Object.keys(doc.let ?? {}).length + Object.keys(doc.functions ?? {}).length)} defaultOpen={Boolean(doc.let || doc.functions)}>
            <div className="pcg-subhead">Lets</div>
            <LetsEditor path="let" lets={doc.let ?? {}} />
            <div className="pcg-subhead">Functions</div>
            <FunctionsEditor doc={doc} />
          </Card>
          <InputsCard doc={doc} />
          <OutputsCard doc={doc} diagnostics={diagnostics} />
          <PatchesCard doc={doc} />
          <ScriptCard doc={doc} />
          <DiagnosticsCard diagnostics={diagnostics} file={path} />
          <Card title="Dependencies" icon="layers" subtitle={deps ? `${deps.nodes.length} nodes` : ''} defaultOpen={false}>
            {deps && (
              <DependencyView
                graph={deps}
                onSelect={(id) => {
                  if (id.startsWith(`${instance}.`)) {
                    const out = id.slice(instance.length + 1).replace(/@.*$/, '');
                    if (getAtPath(doc, ['outputs', out]) !== undefined) reveal(formatJsonPath(['outputs', out]));
                  }
                }}
              />
            )}
          </Card>
        </>
      )}
      <RawJsonCard text={text} parseError={parseError} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                               */

function TopBar() {
  const path = useProcgen((s) => s.path);
  const saveState = useProcgen((s) => s.saveState);
  const past = useProcgen((s) => s.past.length);
  const future = useProcgen((s) => s.future.length);
  const undo = useProcgen((s) => s.undo);
  const redo = useProcgen((s) => s.redo);
  const close = useProcgen((s) => s.close);
  return (
    <div className="pcg-bar">
      <Icon name="layers" size={13} />
      <strong className="pcg-bar-path">{path ?? 'Procedural'}</strong>
      {path && <span className={`pcg-save is-${saveState}`}>{saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save failed' : 'Saving…'}</span>}
      <span className="pcg-spacer" />
      <IconButton icon="undo" title="Undo" size={13} disabled={!past} onClick={undo} />
      <IconButton icon="redo" title="Redo" size={13} disabled={!future} onClick={redo} />
      {path && <IconButton icon="x" title="Close generator" size={13} onClick={close} />}
    </div>
  );
}

export function ProcgenPanel() {
  useEffect(() => attachProcgenSync(), []);
  return (
    <div className="pcg-root" data-dock-panel-body="procgen">
      <TopBar />
      <div className="pcg-columns">
        <FileColumn />
        <Inspector />
        <PreviewPane />
      </div>
    </div>
  );
}

